import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Calendar, ChevronLeft, ChevronRight, Check, X, AlertCircle, 
  Gift, Clock, DollarSign, Ban, RotateCcw, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YearEndReview = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [reviewData, setReviewData] = useState({});
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchYearEndData();
  }, [selectedYear]);

  const fetchYearEndData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/leave-allowances/year-end-review/${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
        
        // Initialize review data for each employee
        const initialReviewData = {};
        data.forEach(emp => {
          initialReviewData[emp.allowance_id] = {
            action: emp.year_end_action || 'carry_over',
            days_to_carry: emp.days_to_carry || 0,
            days_to_pay: emp.days_to_pay || 0,
            days_forfeited: emp.days_forfeited || 0,
            notes: emp.year_end_notes || ''
          };
        });
        setReviewData(initialReviewData);
      } else {
        toast.error('Failed to fetch year-end data');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error fetching data');
    }
    setLoading(false);
  };

  const handleReviewSubmit = async (allowanceId, daysRemaining) => {
    const data = reviewData[allowanceId];
    if (!data) return;

    // Validate
    const total = (data.days_to_carry || 0) + (data.days_to_pay || 0) + (data.days_forfeited || 0);
    if (Math.abs(total - daysRemaining) > 0.5) {
      toast.error(`Days must total ${daysRemaining}. Currently: ${total}`);
      return;
    }

    setProcessing(allowanceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/leave-allowances/${allowanceId}/year-end-review`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success('Year-end review processed successfully');
        fetchYearEndData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process review');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error processing review');
    }
    setProcessing(null);
  };

  const handleResetReview = async (allowanceId) => {
    if (!window.confirm('Are you sure you want to reset this year-end review? This will also reset the carried-over days for next year.')) {
      return;
    }

    setProcessing(allowanceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/leave-allowances/${allowanceId}/reset-year-end`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Review reset successfully');
        fetchYearEndData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset review');
      }
    } catch (error) {
      toast.error('Error resetting review');
    }
    setProcessing(null);
  };

  const updateReviewField = (allowanceId, field, value) => {
    setReviewData(prev => ({
      ...prev,
      [allowanceId]: {
        ...prev[allowanceId],
        [field]: value
      }
    }));
  };

  const autoDistribute = (allowanceId, daysRemaining, action) => {
    const newData = { 
      action, 
      days_to_carry: 0, 
      days_to_pay: 0, 
      days_forfeited: 0,
      notes: reviewData[allowanceId]?.notes || ''
    };

    if (action === 'carry_over') {
      newData.days_to_carry = daysRemaining;
    } else if (action === 'pay_out') {
      newData.days_to_pay = daysRemaining;
    } else if (action === 'forfeit') {
      newData.days_forfeited = daysRemaining;
    }

    setReviewData(prev => ({
      ...prev,
      [allowanceId]: newData
    }));
  };

  // Group employees by department
  const groupedEmployees = employees.reduce((acc, emp) => {
    const dept = emp.department || 'Unassigned';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(emp);
    return acc;
  }, {});

  const stats = {
    total: employees.length,
    pending: employees.filter(e => e.year_end_status !== 'processed').length,
    processed: employees.filter(e => e.year_end_status === 'processed').length,
    totalDaysRemaining: employees.reduce((sum, e) => sum + Math.max(0, e.days_remaining), 0)
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Year-End Leave Review</h1>
          <p className="text-gray-500 mt-1">Review and process remaining leave for your team</p>
        </div>
        
        {/* Year Selector */}
        <div className="flex items-center gap-2 bg-white rounded-lg border shadow-sm p-1">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedYear(y => y - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-4 py-2 font-semibold text-lg min-w-[80px] text-center">
            {selectedYear}
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedYear(y => y + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-gray-500">Total Employees</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-amber-50 rounded-xl shadow-sm border border-amber-200 p-4">
          <div className="text-sm text-amber-600">Pending Review</div>
          <div className="text-2xl font-bold text-amber-700">{stats.pending}</div>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-4">
          <div className="text-sm text-green-600">Processed</div>
          <div className="text-2xl font-bold text-green-700">{stats.processed}</div>
        </div>
        <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-4">
          <div className="text-sm text-blue-600">Total Days Remaining</div>
          <div className="text-2xl font-bold text-blue-700">{stats.totalDaysRemaining.toFixed(1)}</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Year-End Process:</strong> For each employee with remaining leave, choose one of three options:
          <ul className="mt-2 space-y-1 ml-4">
            <li><strong>Carry Over</strong> - Days transfer to {selectedYear + 1}'s allowance</li>
            <li><strong>Pay Out</strong> - Employee receives payment for unused days</li>
            <li><strong>Forfeit</strong> - Days are lost (not recommended without prior notice)</li>
          </ul>
          You can also split the days between options.
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading year-end data...</p>
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No employees found for year-end review</p>
        </div>
      ) : (
        /* Employee List by Department */
        Object.entries(groupedEmployees).map(([department, deptEmployees]) => (
          <div key={department} className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-500"></span>
              {department}
              <span className="text-sm font-normal text-gray-500">({deptEmployees.length})</span>
            </h2>
            
            <div className="space-y-3">
              {deptEmployees.map(emp => {
                const isExpanded = expandedEmployee === emp.allowance_id;
                const isProcessed = emp.year_end_status === 'processed';
                const data = reviewData[emp.allowance_id] || {};
                
                return (
                  <div 
                    key={emp.allowance_id}
                    className={`bg-white rounded-xl shadow-sm border transition-all ${
                      isProcessed ? 'border-green-200 bg-green-50/30' : 'hover:shadow-md'
                    }`}
                  >
                    {/* Employee Row */}
                    <div 
                      className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer"
                      onClick={() => setExpandedEmployee(isExpanded ? null : emp.allowance_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          isProcessed ? 'bg-green-500' : 'bg-teal-500'
                        }`}>
                          {emp.employee_name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{emp.employee_name}</div>
                          <div className="text-sm text-gray-500">
                            {emp.total_allowance} days total • {emp.days_used} used
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Days Remaining Badge */}
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          emp.days_remaining > 0 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {emp.days_remaining > 0 ? `${emp.days_remaining} days remaining` : 'No days remaining'}
                        </div>
                        
                        {/* Status Badge */}
                        {isProcessed && (
                          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
                            <Check className="w-4 h-4" />
                            Processed
                          </div>
                        )}
                        
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t px-4 py-4 bg-gray-50/50">
                        {/* Allowance Breakdown */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 border">
                            <div className="text-xs text-gray-500 uppercase">Base</div>
                            <div className="text-lg font-semibold">{emp.base_allowance} days</div>
                            {emp.is_pro_rated && (
                              <div className="text-xs text-amber-600">Pro-rated</div>
                            )}
                          </div>
                          <div className="bg-white rounded-lg p-3 border">
                            <div className="text-xs text-gray-500 uppercase">Extra (Charity etc.)</div>
                            <div className="text-lg font-semibold text-purple-600">+{emp.extra_days_total} days</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border">
                            <div className="text-xs text-gray-500 uppercase">Carried Over</div>
                            <div className="text-lg font-semibold text-blue-600">+{emp.carried_over_days} days</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border">
                            <div className="text-xs text-gray-500 uppercase">Used</div>
                            <div className="text-lg font-semibold text-gray-600">{emp.days_used} days</div>
                          </div>
                          <div className={`rounded-lg p-3 border ${
                            emp.days_remaining > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white'
                          }`}>
                            <div className="text-xs text-gray-500 uppercase">Remaining</div>
                            <div className={`text-lg font-semibold ${
                              emp.days_remaining > 0 ? 'text-amber-700' : 'text-gray-600'
                            }`}>
                              {emp.days_remaining} days
                            </div>
                          </div>
                        </div>
                        
                        {/* Year-End Actions - Only show if days remaining */}
                        {emp.days_remaining > 0 && !isProcessed && (
                          <div className="bg-white rounded-lg border p-4">
                            <h4 className="font-semibold text-gray-900 mb-4">Year-End Decision</h4>
                            
                            {/* Quick Actions */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <Button
                                size="sm"
                                variant={data.action === 'carry_over' && data.days_to_carry === emp.days_remaining ? 'default' : 'outline'}
                                className={data.action === 'carry_over' && data.days_to_carry === emp.days_remaining ? 'bg-blue-600 hover:bg-blue-700' : ''}
                                onClick={(e) => { e.stopPropagation(); autoDistribute(emp.allowance_id, emp.days_remaining, 'carry_over'); }}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Carry All Over
                              </Button>
                              <Button
                                size="sm"
                                variant={data.action === 'pay_out' && data.days_to_pay === emp.days_remaining ? 'default' : 'outline'}
                                className={data.action === 'pay_out' && data.days_to_pay === emp.days_remaining ? 'bg-green-600 hover:bg-green-700' : ''}
                                onClick={(e) => { e.stopPropagation(); autoDistribute(emp.allowance_id, emp.days_remaining, 'pay_out'); }}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Pay All Out
                              </Button>
                              <Button
                                size="sm"
                                variant={data.action === 'forfeit' && data.days_forfeited === emp.days_remaining ? 'default' : 'outline'}
                                className={data.action === 'forfeit' && data.days_forfeited === emp.days_remaining ? 'bg-red-600 hover:bg-red-700' : ''}
                                onClick={(e) => { e.stopPropagation(); autoDistribute(emp.allowance_id, emp.days_remaining, 'forfeit'); }}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Forfeit All
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  updateReviewField(emp.allowance_id, 'action', 'mixed');
                                }}
                              >
                                <Gift className="w-4 h-4 mr-1" />
                                Split/Mix
                              </Button>
                            </div>
                            
                            {/* Custom Split Fields */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div>
                                <Label className="text-sm text-blue-700">Days to Carry Over</Label>
                                <Input
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  max={emp.days_remaining}
                                  value={data.days_to_carry || 0}
                                  onChange={(e) => updateReviewField(emp.allowance_id, 'days_to_carry', parseFloat(e.target.value) || 0)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-green-700">Days to Pay Out</Label>
                                <Input
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  max={emp.days_remaining}
                                  value={data.days_to_pay || 0}
                                  onChange={(e) => updateReviewField(emp.allowance_id, 'days_to_pay', parseFloat(e.target.value) || 0)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-red-700">Days to Forfeit</Label>
                                <Input
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  max={emp.days_remaining}
                                  value={data.days_forfeited || 0}
                                  onChange={(e) => updateReviewField(emp.allowance_id, 'days_forfeited', parseFloat(e.target.value) || 0)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            
                            {/* Total Check */}
                            <div className={`text-sm mb-4 p-2 rounded ${
                              Math.abs((data.days_to_carry || 0) + (data.days_to_pay || 0) + (data.days_forfeited || 0) - emp.days_remaining) < 0.1
                                ? 'bg-green-50 text-green-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              Total: {((data.days_to_carry || 0) + (data.days_to_pay || 0) + (data.days_forfeited || 0)).toFixed(1)} / {emp.days_remaining} days
                            </div>
                            
                            {/* Notes */}
                            <div className="mb-4">
                              <Label className="text-sm">Notes (optional)</Label>
                              <Input
                                placeholder="Add any notes about this decision..."
                                value={data.notes || ''}
                                onChange={(e) => updateReviewField(emp.allowance_id, 'notes', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1"
                              />
                            </div>
                            
                            {/* Submit Button */}
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleReviewSubmit(emp.allowance_id, emp.days_remaining); }}
                              disabled={processing === emp.allowance_id}
                              className="bg-teal-600 hover:bg-teal-700"
                            >
                              {processing === emp.allowance_id ? 'Processing...' : 'Confirm Year-End Decision'}
                            </Button>
                          </div>
                        )}
                        
                        {/* Already Processed */}
                        {isProcessed && (
                          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                            <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                              <Check className="w-5 h-5" />
                              Year-End Processed
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <div className="text-gray-500">Carried Over</div>
                                <div className="font-semibold text-blue-700">{emp.days_to_carry} days</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Paid Out</div>
                                <div className="font-semibold text-green-700">{emp.days_to_pay} days</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Forfeited</div>
                                <div className="font-semibold text-red-700">{emp.days_forfeited} days</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Processed By</div>
                                <div className="font-semibold">{emp.year_end_processed_by}</div>
                              </div>
                            </div>
                            {emp.year_end_notes && (
                              <div className="text-sm text-gray-600 mb-3">
                                <strong>Notes:</strong> {emp.year_end_notes}
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleResetReview(emp.allowance_id); }}
                              disabled={processing === emp.allowance_id}
                              className="border-amber-500 text-amber-600 hover:bg-amber-50"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reset Review
                            </Button>
                          </div>
                        )}
                        
                        {/* No days remaining */}
                        {emp.days_remaining <= 0 && !isProcessed && (
                          <div className="bg-gray-50 rounded-lg border p-4 text-center text-gray-500">
                            <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                            <p>All leave days have been used. No year-end action required.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default YearEndReview;
